import { apiDelete, apiGetJSON, apiPatchJSON, apiPostJSON, apiPostJSONDetailed, apiPostMultipartDetailed, saveAPIKey, saveSelectedAccountID } from "../client.js";
import { defaultClientAuthToken, isSameOriginAuthToken } from "../core/client-auth.js";
import { cancelRequest, captureRequest } from "../core/request-coordinator.js";
import { captureDecisionDraftInput, clearDecisionDraft, collectDecisionResponseValues } from "../domain/decision-drafts.js";
import { defaultPlaygroundFilename, findPlaygroundRoute, playgroundCapability, playgroundSupportsImageResponseFormat } from "../inference-playground.js";
import {
  cancelThreadComponentRequests,
  handleThreadComponentClick,
  handleThreadComponentInput,
  handleThreadComponentSubmit,
  loadThreadComponent,
  refreshThreadComponent
} from "../thread-component.js";
import {
  cancelTurnDetailRequests,
  handleTurnDetailClick,
  refreshTurnDetail
} from "../turn-detail-component.js";
import {
  cancelWorkspaceContentRequests,
  createWorkspaceContentState,
  handleWorkspaceContentClick,
  handleWorkspaceContentDragDrop,
  handleWorkspaceContentInput,
  loadWorkspaceContentTree
} from "../workspace-content-component.js";

export async function handleAdminSubmit(state, event, options = {}) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }
  event.preventDefault();
  try {
    if (state.route.id === "thread-detail") {
      const handled = await handleThreadComponentSubmit(state.threadDetail, form, options.adminThreadComponentOptions?.());
      if (handled) {
        return;
      }
    }
    switch (form.id) {
    case "auth-form":
      await submitAuth(state, form, options);
      break;
    case "thread-form":
      await submitThread(state, form, options);
      break;
    case "agent-form":
      await submitAgent(state, form, options);
      break;
    case "workspace-form":
      await submitWorkspace(state, form, options);
      break;
    case "turn-form":
      await submitTurn(state, form, options);
      break;
    case "inference-credential-form":
      await submitInferenceCredential(state, form, options);
      break;
    case "inference-route-form":
      await submitInferenceRoute(state, form, options);
      break;
    case "inference-playground-form":
      await submitInferencePlayground(state, form, options);
      break;
    default:
      break;
    }
  } catch (error) {
    const formKey = options.formKeyForID?.(form.id) || "";
    if (formKey) {
      state.formErrors[formKey] = error.message;
    }
    options.setStatus?.(error.message, "error");
    options.render?.();
  }
}

export function handleAdminFieldInput(state, event, options = {}) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
    return;
  }
  if (state.route.id === "thread-detail" && handleThreadComponentInput(state.threadDetail, target)) {
    if (
      (target instanceof HTMLInputElement && target.type === "file")
      || target.name === "route_id"
      || target.name === "model"
      || target.name === "context_filter_text"
      || (target instanceof HTMLSelectElement && target.name === "capability_family")
    ) {
      options.render?.();
    }
    return;
  }
  if (handleWorkspaceContentInput(state.workspaceContent, target)) {
    if (target instanceof HTMLInputElement && target.type === "file") {
      options.render?.();
    }
    return;
  }
  if (target.id === "promotion-description" && state.promotionConfirm) {
    state.promotionConfirm.description = target.value;
    return;
  }
  if (target.name === "selected_account_id" && target.closest("[data-account-selector]")) {
    void options.selectAccount?.(target.value);
    return;
  }
  const decisionForm = target.closest("[data-decision-id]");
  if (decisionForm) {
    captureDecisionDraftInput(state.decisionDrafts, target);
    return;
  }
  const form = target.closest("form");
  if (!(form instanceof HTMLFormElement) || !target.name) {
    return;
  }
  if (form.id === "auth-form") {
    state.authInput = target instanceof HTMLInputElement && target.type === "checkbox" ? target.checked : target.value;
    state.authStatus = { message: "", tone: "idle" };
    return;
  }
  const formKey = options.formKeyForID?.(form.id) || "";
  if (!formKey) {
    return;
  }
  if (target instanceof HTMLInputElement && target.type === "file") {
    state.formErrors[formKey] = "";
    return;
  }
  state.forms[formKey][target.name] = target instanceof HTMLInputElement && target.type === "checkbox"
    ? target.checked
    : target.value;
  if (formKey === "agent") {
    state.forms.agent = options.normalizeAgentDraft?.(state.forms.agent) || state.forms.agent;
    if (
      target.name === "execution_mode"
      || target.name === "adapter_id"
      || target.name === "harness_id"
      || target.name.endsWith(".route_id")
    ) {
      options.render?.();
    }
  }
  if (formKey === "thread") {
    state.forms.thread = options.normalizeThreadDraft?.(state.forms.thread) || state.forms.thread;
    if (target.name === "agent_id") {
      options.render?.();
    }
  }
  if (formKey === "workspace" && target.name === "repo_source_kind") {
    options.render?.();
  }
  if (formKey === "inferenceCredential" && target.name === "provider") {
    const providerDefaults = {
      openai:     { base_url: "https://api.openai.com/v1",      scopes_csv: "responses,chat.completions,embeddings,audio.transcriptions,audio.translations,audio.speech,images.generate" },
      openrouter: { base_url: "https://openrouter.ai/api/v1",   scopes_csv: "chat.completions,embeddings" },
      groq:       { base_url: "https://api.groq.com/openai/v1", scopes_csv: "chat.completions,embeddings,audio.transcriptions,audio.translations,audio.speech" },
      xai:        { base_url: "https://api.x.ai/v1",            scopes_csv: "chat.completions,embeddings" },
      compatible: { base_url: "",                                 scopes_csv: "" }
    };
    const defaults = providerDefaults[target.value];
    if (defaults) Object.assign(state.forms.inferenceCredential, defaults);
    options.render?.();
  }
  if (formKey === "inferenceCredential" && target.name === "name" && !state.inferenceCredentialRefManuallyEdited) {
    const slug = options.slugify?.(target.value) || "";
    state.forms.inferenceCredential.credential_ref = slug;
    const refInput = document.getElementById("inference-credential-ref");
    if (refInput) refInput.value = slug;
  }
  if (formKey === "inferenceCredential" && target.name === "credential_ref") {
    state.inferenceCredentialRefManuallyEdited = true;
  }
  if (formKey === "inferencePlayground" && target.name === "route_id") {
    state.inferencePlaygroundResponse = null;
    state.inferencePlaygroundShowRawJSON = false;
    options.render?.();
  }
  state.formErrors[formKey] = "";
}

export async function handleAdminClick(state, event, options = {}) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) {
    return;
  }

  try {
    const link = target.closest("[data-route]");
    if (link) {
      event.preventDefault();
      state.router.navigate(link.getAttribute("href"));
      return;
    }

    const refresh = target.closest("[data-action='refresh']");
    if (refresh) {
      event.preventDefault();
      options.setStatus?.("Refreshing...", "busy");
      await options.handleRouteChange?.(state.route);
      options.setStatus?.("Refreshed.", "ok");
      return;
    }

    const installApp = target.closest("[data-action='install-app']");
    if (installApp) {
      event.preventDefault();
      const result = await options.promptPWAInstall?.();
      if (!result || result.outcome === "unavailable") {
        options.setStatus?.("Use your browser's install or Add to Home Screen menu.", "idle");
      } else if (result.outcome === "accepted") {
        options.setStatus?.("Install requested.", "ok");
      } else {
        options.setStatus?.("Install dismissed.", "idle");
      }
      options.render?.();
      return;
    }

    const navToggle = target.closest("[data-action='toggle-nav']");
    if (navToggle) {
      event.preventDefault();
      state.navCollapsed = !state.navCollapsed;
      localStorage.setItem("nav-collapsed", state.navCollapsed ? "1" : "0");
      options.render?.();
      return;
    }

    const themeToggle = target.closest("[data-action='toggle-theme']");
    if (themeToggle) {
      event.preventDefault();
      options.toggleTheme?.();
      options.render?.();
      return;
    }

    const settingsToggle = target.closest("[data-action='toggle-settings']");
    if (settingsToggle) {
      event.preventDefault();
      state.settingsOpen = !state.settingsOpen;
      options.render?.();
      return;
    }

    const settingsClose = target.closest("[data-action='close-settings']");
    if (settingsClose) {
      state.settingsOpen = false;
      options.render?.();
      return;
    }

    const toggleThreadContent = target.closest("[data-action='toggle-thread-content-panel']");
    if (toggleThreadContent) {
      event.preventDefault();
      await toggleThreadContentPanel(state, options);
      return;
    }

    const drawerToggle = target.closest("[data-action='toggle-detail-drawer']");
    if (drawerToggle) {
      event.preventDefault();
      state.detailDrawerOpen = !state.detailDrawerOpen;
      options.render?.();
      return;
    }

    const drawerClose = target.closest("[data-action='close-drawer']");
    if (drawerClose) {
      state.detailDrawerOpen = false;
      options.render?.();
      return;
    }

    const drawerOverlay = target.closest(".detail-drawer-overlay");
    if (drawerOverlay) {
      state.detailDrawerOpen = false;
      options.render?.();
      return;
    }

    const threadFilterButton = target.closest("[data-thread-filter]");
    if (threadFilterButton) {
      event.preventDefault();
      state.threadFilter = threadFilterButton.getAttribute("data-thread-filter");
      options.render?.();
      return;
    }

    const diagnosticFilterButton = target.closest("[data-diagnostic-filter]");
    if (diagnosticFilterButton) {
      event.preventDefault();
      state.diagnosticFilter = diagnosticFilterButton.getAttribute("data-diagnostic-filter");
      options.render?.();
      return;
    }

    const diagnosticCreateToggle = target.closest("[data-action='toggle-diagnostic-create']");
    if (diagnosticCreateToggle) {
      event.preventDefault();
      state.diagnosticCreateOpen = !state.diagnosticCreateOpen;
      options.render?.();
      return;
    }

    const threadCreateToggle = target.closest("[data-action='toggle-thread-create']");
    if (threadCreateToggle) {
      event.preventDefault();
      state.threadCreateOpen = !state.threadCreateOpen;
      options.render?.();
      return;
    }

    const agentCreateToggle = target.closest("[data-action='toggle-agent-create']");
    if (agentCreateToggle) {
      event.preventDefault();
      if (state.agentCreateOpen || state.editingAgentID) {
        state.agentCreateOpen = false;
        state.editingAgentID = "";
        state.forms.agent = options.normalizeAgentDraft?.(options.defaultAgentDraft?.()) || state.forms.agent;
        state.formErrors.agent = "";
      } else {
        state.agentCreateOpen = true;
      }
      options.render?.();
      return;
    }

    if (state.route.id === "thread-detail") {
      const handled = await handleThreadComponentClick(state.threadDetail, target, options.adminThreadComponentOptions?.());
      if (handled) {
        event.preventDefault();
        return;
      }
    }

    if (state.route.id === "turn-detail") {
      const handled = await handleTurnDetailClick(state.turnDetail, target, options.adminTurnDetailOptions?.());
      if (handled) {
        event.preventDefault();
        return;
      }
    }

    if (state.workspaceContent.workspaceID) {
      const workspaceItem = options.lookupByID?.(state.workspaces, state.workspaceContent.workspaceID) || null;
      const handled = await handleWorkspaceContentClick(
        state.workspaceContent,
        target,
        options.adminWorkspaceContentOptions?.(workspaceItem)
      );
      if (handled) {
        event.preventDefault();
        return;
      }
    }

    if (await handleThreadAndDecisionActions(state, target, event, options)) {
      return;
    }
    if (await handleAgentActions(state, target, event, options)) {
      return;
    }
    if (await handleInferenceActions(state, target, event, options)) {
      return;
    }
    if (await handleWorkspaceActions(state, target, event, options)) {
      return;
    }
  } catch (error) {
    options.setStatus?.(error.message || "request failed", "error");
    options.render?.();
  }
}

async function submitAuth(state, form, options) {
  const data = new FormData(form);
  const submittedAPIKey = String(data.get("api_key") || "").trim();
  const nextAPIKey = submittedAPIKey || defaultClientAuthToken();
  if (!nextAPIKey) {
    state.apiKey = saveAPIKey("");
    state.accounts = [];
    state.selectedAccountID = saveSelectedAccountID("");
    state.authInput = "";
    state.authStatus = { message: "", tone: "idle" };
    state.settingsOpen = false;
    cancelRequest(state.requests.route);
    cancelRequest(state.requests.collections);
    cancelTurnDetailRequests(state.turnDetail);
    cancelThreadComponentRequests(state.threadDetail);
    cancelWorkspaceContentRequests(state.workspaceContent);
    options.setStatus?.("Enter a batch-1 bearer API key to unlock the console.", "idle");
    state.editingAgentID = "";
    state.editingWorkspaceID = "";
    state.forms.agent = options.normalizeAgentDraft?.(options.defaultAgentDraft?.()) || state.forms.agent;
    state.forms.workspace = options.defaultWorkspaceDraft?.() || state.forms.workspace;
    state.formErrors.agent = "";
    state.formErrors.workspace = "";
    state.harnesses = [];
    state.promotions = [];
    state.pendingDecisions = [];
    state.workspaceDiagnosticsByID = {};
    state.inferenceEnabled = false;
    state.inferenceCredentials = [];
    state.inferenceRoutes = [];
    state.inferenceTraffic = [];
    state.editingInferenceCredentialID = "";
    state.editingInferenceRouteID = "";
    state.inferenceCredentialFormOpen = false;
    state.inferenceRouteFormOpen = false;
    state.inferenceTrafficFilter = "all";
    state.inferenceTrafficExpanded = false;
    state.inferencePlaygroundShowRawJSON = false;
    state.inferenceCredentialRefManuallyEdited = false;
    state.forms.inferenceCredential = options.defaultInferenceCredentialDraft?.() || state.forms.inferenceCredential;
    state.forms.inferenceRoute = options.defaultInferenceRouteDraft?.() || state.forms.inferenceRoute;
    state.forms.inferencePlayground = options.defaultInferencePlaygroundDraft?.() || state.forms.inferencePlayground;
    state.formErrors.inferenceCredential = "";
    state.formErrors.inferenceRoute = "";
    state.formErrors.inferencePlayground = "";
    state.inferencePlaygroundResponse = null;
    options.resetThreadDetail?.();
    options.resetTurnDetail?.();
    options.resetWorkspaceContentPanel?.();
    state.agents = [];
    state.workspaces = [];
    state.threads = [];
    state.turns = [];
    options.stopLiveResources?.();
    options.render?.();
    return;
  }

  state.authStatus = { message: "Checking API key...", tone: "busy" };
  options.setStatus?.("Checking API key...", "busy");
  options.render?.();

  try {
    await validateSubmittedAPIKey(nextAPIKey, options);
  } catch (error) {
    state.authInput = submittedAPIKey;
    state.authStatus = { message: authValidationMessage(error), tone: "error" };
    if (state.apiKey) {
      state.settingsOpen = true;
    }
    options.setStatus?.(state.authStatus.message, "error");
    options.render?.();
    return;
  }

  state.apiKey = saveAPIKey(submittedAPIKey);
  state.authInput = isSameOriginAuthToken(state.apiKey) ? "" : state.apiKey;
  state.authStatus = { message: "API key verified.", tone: "ok" };
  state.settingsOpen = false;
  options.setStatus?.("Connecting to the runtime API...", "busy");
  await options.handleRouteChange?.(state.route);
  options.setStatus?.("Runtime console unlocked.", "ok");
}

async function validateSubmittedAPIKey(apiKey, options) {
  if (typeof options.validateAPIKey === "function") {
    await options.validateAPIKey(apiKey);
    return;
  }
  await apiGetJSON(apiKey, "/v1/agents?limit=1");
}

function authValidationMessage(error) {
  if (error?.status === 401) {
    return "Invalid API key.";
  }
  if (error?.status === 403) {
    return "This token is not authorized for the runtime console.";
  }
  return error?.message || "Unable to verify API key.";
}

async function submitAgent(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "agent");
  state.forms.agent = options.normalizeAgentDraft?.(state.forms.agent) || state.forms.agent;
  const draft = state.forms.agent;
  const parsedOverrides = options.parseRuntimeConfig?.(draft.runtime_config) || { value: null, error: "" };
  if (parsedOverrides.error) {
    throw new Error(parsedOverrides.error);
  }
  const runtimeConfig = options.buildAgentRuntimeConfig?.(draft, parsedOverrides.value);
  const agentConfigError = options.validateAgentDraft?.(draft, runtimeConfig);
  if (agentConfigError) {
    throw new Error(agentConfigError);
  }
  const payload = {
    name: draft.name,
    execution_mode: String(draft.execution_mode || "harness"),
    adapter_id: draft.execution_mode === "llm" ? "" : draft.adapter_id,
    model: draft.execution_mode === "llm" ? "" : draft.model,
    system_prompt: draft.system_prompt,
    credential_ref: draft.credential_ref
  };
  if (runtimeConfig) {
    payload.runtime_config = runtimeConfig;
  } else if (state.editingAgentID) {
    payload.runtime_config = {};
  }
  const editing = state.editingAgentID;
  options.setStatus?.(editing ? "Updating agent..." : "Creating agent...", "busy");
  if (editing) {
    await apiPatchJSON(state.apiKey, `/v1/agents/${encodeURIComponent(editing)}`, payload);
  } else {
    await apiPostJSON(state.apiKey, "/v1/agents", payload);
  }
  state.editingAgentID = "";
  state.forms.agent = options.normalizeAgentDraft?.(options.defaultAgentDraft?.()) || state.forms.agent;
  state.formErrors.agent = "";
  await options.loadBaseData?.();
  options.setStatus?.(editing ? "Agent updated." : "Agent created.", "ok");
  options.render?.();
}

async function submitWorkspace(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "workspace");
  const draft = state.forms.workspace;
  const editing = state.editingWorkspaceID;
  const workspaceError = options.validateWorkspaceDraft?.(draft, { editing });
  if (workspaceError) {
    throw new Error(workspaceError);
  }
  options.setStatus?.(editing ? "Updating workspace..." : "Creating workspace...", "busy");
  if (editing) {
    await apiPatchJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(editing)}`, {
      name: draft.name,
      default_branch: draft.default_branch,
      authoring_root_path: draft.authoring_root_path
    });
    options.clearWorkspaceDiagnostics?.(editing);
  } else {
    const payload = {
      name: draft.name,
      repo_source_kind: draft.repo_source_kind,
      default_branch: draft.default_branch
    };
    if (draft.repo_source_kind === "local_authoring") {
      payload.authoring_root_path = draft.authoring_root_path;
    } else {
      payload.remote_name = draft.remote_name;
      payload.remote_url = draft.remote_url;
      payload.fetch_auth_ref = draft.fetch_auth_ref;
      payload.push_auth_ref = draft.push_auth_ref;
    }
    await apiPostJSON(state.apiKey, "/v1/workspaces", payload);
  }
  state.editingWorkspaceID = "";
  state.forms.workspace = options.defaultWorkspaceDraft?.() || state.forms.workspace;
  state.formErrors.workspace = "";
  await options.loadBaseData?.();
  options.setStatus?.(editing ? "Workspace updated." : "Workspace created. Initialize it before use.", "ok");
  options.render?.();
}

async function submitThread(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "thread");
  state.forms.thread = options.normalizeThreadDraft?.(state.forms.thread) || state.forms.thread;
  const draft = state.forms.thread;
  const threadError = options.validateThreadDraft?.(draft);
  if (threadError) {
    throw new Error(threadError);
  }
  options.setStatus?.("Creating thread...", "busy");
  const payload = {
    agent_id: draft.agent_id,
    base_ref: draft.base_ref
  };
  if (String(draft.workspace_id || "").trim()) {
    payload.workspace_id = draft.workspace_id;
  }
  const created = await apiPostJSON(state.apiKey, "/v1/threads", payload);
  state.forms.thread = options.defaultThreadDraft?.() || state.forms.thread;
  state.formErrors.thread = "";
  await options.loadBaseData?.();
  options.setStatus?.("Thread created.", "ok");
  state.router.navigate(`/threads/${encodeURIComponent(created.id)}`);
}

async function submitTurn(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "turn");
  const draft = state.forms.turn;
  options.setStatus?.("Queueing ad hoc diagnostic turn...", "busy");
  const created = await apiPostJSON(state.apiKey, "/v1/turns", {
    agent_id: draft.agent_id,
    workspace_id: draft.workspace_id,
    prompt: draft.prompt,
    base_ref: draft.base_ref,
    branch_name: draft.branch_name
  });
  state.forms.turn = options.defaultTurnDraft?.() || state.forms.turn;
  state.formErrors.turn = "";
  await options.loadBaseData?.();
  options.setStatus?.("Ad hoc turn queued.", "ok");
  state.router.navigate(`/turns/${encodeURIComponent(created.id)}`);
}

async function submitInferenceCredential(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "inferenceCredential");
  const draft = state.forms.inferenceCredential;
  const editing = state.editingInferenceCredentialID;
  const error = options.validateInferenceCredentialDraft?.(draft, { editing });
  if (error) {
    throw new Error(error);
  }
  const payload = {
    provider: String(draft.provider || "openai").trim(),
    name: String(draft.name || "").trim(),
    credential_ref: String(draft.credential_ref || "").trim(),
    base_url: String(draft.base_url || "").trim(),
    scopes: parseCSVString(draft.scopes_csv),
    enabled: Boolean(draft.enabled)
  };
  const secret = String(draft.secret || "").trim();
  if (editing) {
    if (secret) {
      payload.secret = secret;
    }
    options.setStatus?.("Updating credential...", "busy");
    await apiPatchJSON(state.apiKey, `/v1/inference/credentials/${encodeURIComponent(editing)}`, payload);
  } else {
    payload.secret = secret;
    options.setStatus?.("Creating credential...", "busy");
    await apiPostJSON(state.apiKey, "/v1/inference/credentials", payload);
  }
  state.editingInferenceCredentialID = "";
  state.inferenceCredentialFormOpen = false;
  state.inferenceCredentialRefManuallyEdited = false;
  state.forms.inferenceCredential = options.defaultInferenceCredentialDraft?.() || state.forms.inferenceCredential;
  state.formErrors.inferenceCredential = "";
  await options.loadBaseData?.();
  options.setStatus?.(editing ? "Credential updated." : "Credential created.", "ok");
  options.render?.();
}

async function submitInferenceRoute(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "inferenceRoute");
  const draft = state.forms.inferenceRoute;
  const editing = state.editingInferenceRouteID;
  const error = options.validateInferenceRouteDraft?.(draft);
  if (error) {
    throw new Error(error);
  }
  const payload = {
    name: String(draft.name || "").trim(),
    provider_client_id: String(draft.provider_client_id || "openai_compatible").trim() || "openai_compatible",
    credential_id: String(draft.credential_id || "").trim(),
    capability_family: String(draft.capability_family || "responses").trim() || "responses",
    match_mode: String(draft.match_mode || "").trim(),
    match_value: String(draft.match_value || "").trim(),
    advertised_models: parseCSVString(draft.advertised_models_csv),
    base_url_override: String(draft.base_url_override || "").trim(),
    default_model: String(draft.default_model || "").trim(),
    timeout_ms: Number(draft.timeout_ms || 0),
    max_retries: Number(draft.max_retries || 0),
    enabled: Boolean(draft.enabled),
    is_default: Boolean(draft.is_default)
  };
  options.setStatus?.(editing ? "Updating route..." : "Creating route...", "busy");
  if (editing) {
    await apiPatchJSON(state.apiKey, `/v1/inference/routes/${encodeURIComponent(editing)}`, payload);
  } else {
    await apiPostJSON(state.apiKey, "/v1/inference/routes", payload);
  }
  state.editingInferenceRouteID = "";
  state.inferenceRouteFormOpen = false;
  state.forms.inferenceRoute = options.defaultInferenceRouteDraft?.() || state.forms.inferenceRoute;
  state.formErrors.inferenceRoute = "";
  await options.loadBaseData?.();
  options.setStatus?.(editing ? "Route updated." : "Route created.", "ok");
  options.render?.();
}

async function submitInferencePlayground(state, form, options) {
  options.requireAPIKey?.();
  options.captureDraftFromForm?.(form, "inferencePlayground");
  const draft = state.forms.inferencePlayground;
  const routeID = String(draft.route_id || "").trim();
  if (!routeID) {
    throw new Error("route_id is required");
  }
  const selectedRoute = findPlaygroundRoute(state.inferenceRoutes, routeID);
  if (!selectedRoute) {
    throw new Error("selected route was not found");
  }
  const capability = playgroundCapability(selectedRoute);
  options.setStatus?.("Running playground request...", "busy");
  state.inferencePlaygroundLoading = true;
  state.inferencePlaygroundResponse = null;
  options.render?.();
  try {
    if (capability === "responses") {
      const messages = [];
      const systemPrompt = String(draft.system_prompt || "").trim();
      if (systemPrompt) {
        messages.push({ role: "system", content: systemPrompt });
      }
      const prompt = String(draft.prompt || "").trim();
      if (!prompt) {
        throw new Error("prompt is required");
      }
      messages.push({ role: "user", content: prompt });
      const playgroundPayload = {
        route_id: routeID,
        model: String(draft.model || "").trim(),
        messages,
        stream: Boolean(draft.stream)
      };
      const temperature = String(draft.temperature || "").trim();
      if (temperature !== "") playgroundPayload.temperature = Number(temperature);
      const maxTokens = String(draft.max_tokens || "").trim();
      if (maxTokens !== "") playgroundPayload.max_tokens = Number(maxTokens);
      const topP = String(draft.top_p || "").trim();
      if (topP !== "") playgroundPayload.top_p = Number(topP);
      const response = await apiPostJSON(state.apiKey, "/v1/inference/playground/responses", playgroundPayload);
      state.inferencePlaygroundResponse = {
        capability_family: capability,
        request: response.request || null,
        response: response.response
      };
    } else {
      const response = await runCapabilityPlaygroundRequest(state, form, selectedRoute, draft);
      state.inferencePlaygroundResponse = response;
    }
    state.formErrors.inferencePlayground = "";
    await options.loadBaseData?.();
    options.setStatus?.("Playground response received.", "ok");
  } finally {
    state.inferencePlaygroundLoading = false;
  }
  options.render?.();
}

async function runCapabilityPlaygroundRequest(state, form, route, draft) {
  const capability = playgroundCapability(route);
  const headers = { "X-Lytebulb-Route": route.id };
  let execution = null;
  switch (capability) {
  case "embeddings":
    execution = await apiPostJSONDetailed(state.apiKey, "/v1/embeddings", buildEmbeddingPlaygroundPayload(draft), { headers });
    break;
  case "audio_transcription":
    execution = await apiPostMultipartDetailed(state.apiKey, "/v1/audio/transcriptions", buildAudioPlaygroundFormData(form, draft, capability), { headers });
    break;
  case "audio_translation":
    execution = await apiPostMultipartDetailed(state.apiKey, "/v1/audio/translations", buildAudioPlaygroundFormData(form, draft, capability), { headers });
    break;
  case "audio_speech":
    execution = await apiPostJSONDetailed(state.apiKey, "/v1/audio/speech", buildAudioSpeechPlaygroundPayload(draft), { headers });
    break;
  case "image_generation":
    execution = await apiPostJSONDetailed(state.apiKey, "/v1/images/generations", buildImageGenerationPlaygroundPayload(route, draft), { headers });
    break;
  default:
    throw new Error(`unsupported playground capability: ${capability}`);
  }
  return normalizeCapabilityPlaygroundResponse(state, capability, execution);
}

function buildEmbeddingPlaygroundPayload(draft) {
  const input = String(draft.prompt || "").trim();
  if (!input) {
    throw new Error("input is required");
  }
  const payload = {
    input,
    model: String(draft.model || "").trim()
  };
  const dimensions = String(draft.dimensions || "").trim();
  if (dimensions !== "") {
    payload.dimensions = Number(dimensions);
  }
  const encodingFormat = String(draft.encoding_format || "").trim();
  if (encodingFormat) {
    payload.encoding_format = encodingFormat;
  }
  return payload;
}

function buildImageGenerationPlaygroundPayload(route, draft) {
  const prompt = String(draft.prompt || "").trim();
  if (!prompt) {
    throw new Error("prompt is required");
  }
  const payload = {
    prompt,
    model: String(draft.model || "").trim()
  };
  const size = String(draft.size || "").trim();
  if (size) payload.size = size;
  const quality = String(draft.quality || "").trim();
  if (quality) payload.quality = quality;
  const responseFormat = String(draft.response_format || "").trim();
  if (responseFormat && playgroundSupportsImageResponseFormat(route, draft)) payload.response_format = responseFormat;
  const n = String(draft.n || "").trim();
  if (n !== "") payload.n = Number(n);
  return payload;
}

function buildAudioSpeechPlaygroundPayload(draft) {
  const input = String(draft.prompt || "").trim();
  if (!input) {
    throw new Error("input is required");
  }
  const voice = String(draft.voice || "").trim();
  if (!voice) {
    throw new Error("voice is required");
  }
  const payload = {
    input,
    voice,
    model: String(draft.model || "").trim()
  };
  const responseFormat = String(draft.response_format || "").trim();
  if (responseFormat) payload.response_format = responseFormat;
  const speed = String(draft.speed || "").trim();
  if (speed !== "") payload.speed = Number(speed);
  return payload;
}

function buildAudioPlaygroundFormData(form, draft, capability) {
  const payload = new FormData();
  const file = getPlaygroundFile(form);
  if (!file) {
    throw new Error("file is required");
  }
  payload.append("file", file, file.name || "upload");
  appendIfPresent(payload, "model", String(draft.model || "").trim());
  appendIfPresent(payload, "prompt", String(draft.prompt || "").trim());
  appendIfPresent(payload, "response_format", String(draft.response_format || "").trim());
  const temperature = String(draft.temperature || "").trim();
  if (temperature !== "") {
    payload.append("temperature", temperature);
  }
  if (capability === "audio_transcription") {
    appendIfPresent(payload, "language", String(draft.language || "").trim());
  }
  return payload;
}

function getPlaygroundFile(form) {
  const elements = form?.elements;
  if (!elements || typeof elements.namedItem !== "function") {
    return null;
  }
  const input = elements.namedItem("file");
  if (!input || !input.files || input.files.length === 0) {
    return null;
  }
  return input.files[0] || null;
}

function appendIfPresent(formData, name, value) {
  const current = String(value || "").trim();
  if (current) {
    formData.append(name, current);
  }
}

async function normalizeCapabilityPlaygroundResponse(state, capability, execution) {
  const requestID = execution.headers.get("X-Lytebulb-Inference-Request-ID") || "";
  let request = null;
  if (requestID) {
    request = await apiGetJSON(state.apiKey, `/v1/inference/traffic/${encodeURIComponent(requestID)}`);
  }
  if (execution.body instanceof Blob) {
    const downloadURL = await blobToDataURL(execution.body, execution.contentType || "application/octet-stream");
    return {
      capability_family: capability,
      request,
      response: {
        size_bytes: execution.body.size
      },
      content_type: execution.contentType || "application/octet-stream",
      filename: firstNonEmpty(execution.filename, defaultPlaygroundFilename(capability, execution.contentType)),
      download_url: downloadURL
    };
  }
  return {
    capability_family: capability,
    request,
    response: execution.body,
    content_type: execution.contentType || "application/json",
    filename: execution.filename || ""
  };
}

async function blobToDataURL(blob, contentType) {
  const buffer = await blob.arrayBuffer();
  const base64 = bytesToBase64(new Uint8Array(buffer));
  return `data:${contentType || "application/octet-stream"};base64,${base64}`;
}

function bytesToBase64(bytes) {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }
  let out = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const slice = bytes.subarray(index, index + chunkSize);
    out += String.fromCharCode(...slice);
  }
  return btoa(out);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const current = String(value || "").trim();
    if (current) {
      return current;
    }
  }
  return "";
}

async function handleThreadAndDecisionActions(state, target, event, options) {
  const deleteThread = target.closest("[data-action='delete-thread']");
  if (deleteThread) {
    event.preventDefault();
    const currentThread = state.threadDetail.thread;
    if (!currentThread) {
      return true;
    }
    const warning = currentThread.merge_state === "merged" || currentThread.state === "archived"
      ? `Delete thread ${currentThread.id}? This removes its history and draft state.`
      : `Delete thread ${currentThread.id}? This permanently removes the thread, its unmerged draft state, and its worktree.`;
    if (!confirm(warning)) {
      return true;
    }
    options.setStatus?.("Deleting thread...", "busy");
    await apiDelete(state.apiKey, `/v1/threads/${encodeURIComponent(currentThread.id)}`);
    options.stopLiveResources?.();
    await options.loadBaseData?.();
    options.setStatus?.("Thread deleted.", "ok");
    state.router.navigate("/threads");
    return true;
  }

  const mergeAction = target.closest("[data-action='merge-into-target']");
  if (mergeAction) {
    event.preventDefault();
    const branch = mergeAction.getAttribute("data-target-branch") || "main";
    const mode = mergeAction.getAttribute("data-promotion-mode") || "local_merge";
    const threadState = state.threadDetail.thread?.merge_state || "";
    if (threadState === "merge_conflicted") {
      await submitPromotion(state, branch, mode, "", options);
      return true;
    }
    state.promotionConfirm = {
      threadID: state.threadDetail.thread?.id || "",
      mode,
      targetBranch: branch,
      description: ""
    };
    options.render?.();
    return true;
  }

  const submitPromotionConfirm = target.closest("[data-action='submit-promotion-confirm']");
  if (submitPromotionConfirm) {
    event.preventDefault();
    if (!state.promotionConfirm) {
      return true;
    }
    const { targetBranch, mode, description } = state.promotionConfirm;
    state.promotionConfirm = null;
    await submitPromotion(state, targetBranch, mode, description, options);
    return true;
  }

  const cancelPromotionConfirm = target.closest("[data-action='cancel-promotion-confirm']");
  if (cancelPromotionConfirm) {
    event.preventDefault();
    state.promotionConfirm = null;
    options.render?.();
    return true;
  }

  const retryPromotionAction = target.closest("[data-action='retry-promotion']");
  if (retryPromotionAction) {
    event.preventDefault();
    const promotionID = retryPromotionAction.getAttribute("data-promotion-id") || "";
    if (!confirm("Retry the failed remote push?")) {
      return true;
    }
    await retryPromotion(state, promotionID, options);
    return true;
  }

  const cancelPromotionAction = target.closest("[data-action='cancel-promotion']");
  if (cancelPromotionAction) {
    event.preventDefault();
    const promotionID = cancelPromotionAction.getAttribute("data-promotion-id") || "";
    if (!confirm("Cancel local tracking for this remote merge request?")) {
      return true;
    }
    await cancelPromotion(state, promotionID, options);
    return true;
  }

  const decisionAction = target.closest("[data-action='decision-respond']");
  if (decisionAction) {
    event.preventDefault();
    await submitDecisionResponse(state, decisionAction, options);
    return true;
  }

  const decisionCancel = target.closest("[data-action='decision-cancel']");
  if (decisionCancel) {
    event.preventDefault();
    await submitDecisionCancel(state, decisionCancel, options);
    return true;
  }

  return false;
}

async function handleAgentActions(state, target, event, options) {
  const startAgentEdit = target.closest("[data-action='edit-agent']");
  if (startAgentEdit) {
    event.preventDefault();
    const agentID = startAgentEdit.getAttribute("data-agent-id") || "";
    const selectedAgent = options.lookupByID?.(state.agents, agentID) || null;
    if (!selectedAgent) {
      return true;
    }
    state.editingAgentID = selectedAgent.id;
    state.agentCreateOpen = true;
    state.forms.agent = options.normalizeAgentDraft?.(options.agentDraftFromItem?.(selectedAgent)) || state.forms.agent;
    state.formErrors.agent = "";
    options.render?.();
    return true;
  }

  const cancelAgentEdit = target.closest("[data-action='cancel-agent-edit']");
  if (cancelAgentEdit) {
    event.preventDefault();
    state.editingAgentID = "";
    state.agentCreateOpen = false;
    state.forms.agent = options.normalizeAgentDraft?.(options.defaultAgentDraft?.()) || state.forms.agent;
    state.formErrors.agent = "";
    options.render?.();
    return true;
  }

  const deleteAgent = target.closest("[data-action='delete-agent']");
  if (deleteAgent) {
    event.preventDefault();
    const agentID = deleteAgent.getAttribute("data-agent-id") || "";
    const selectedAgent = options.lookupByID?.(state.agents, agentID) || null;
    if (!selectedAgent) {
      return true;
    }
    if (!window.confirm(`Delete agent "${selectedAgent.name}" and all its turns/threads/artifacts?`)) {
      return true;
    }
    options.setStatus?.(`Deleting ${selectedAgent.name}...`, "busy");
    await apiDelete(state.apiKey, `/v1/agents/${encodeURIComponent(agentID)}?force=1`);
    if (state.editingAgentID === agentID) {
      state.editingAgentID = "";
      state.forms.agent = options.normalizeAgentDraft?.(options.defaultAgentDraft?.()) || state.forms.agent;
      state.formErrors.agent = "";
    }
    await options.loadBaseData?.();
    options.setStatus?.("Agent deleted.", "ok");
    options.render?.();
    return true;
  }

  return false;
}

async function handleWorkspaceActions(state, target, event, options) {
  const workspaceCreateToggle = target.closest("[data-action='toggle-workspace-create']");
  if (workspaceCreateToggle) {
    event.preventDefault();
    if (state.workspaceCreateOpen || state.editingWorkspaceID) {
      state.workspaceCreateOpen = false;
      state.editingWorkspaceID = "";
      state.forms.workspace = options.defaultWorkspaceDraft?.() || state.forms.workspace;
      state.formErrors.workspace = "";
    } else {
      state.workspaceCreateOpen = true;
    }
    options.render?.();
    return true;
  }

  const startWorkspaceEdit = target.closest("[data-action='edit-workspace']");
  if (startWorkspaceEdit) {
    event.preventDefault();
    const workspaceID = startWorkspaceEdit.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    const diagnostics = await options.loadWorkspaceDiagnostics?.(selectedWorkspace.id);
    state.editingWorkspaceID = selectedWorkspace.id;
    state.workspaceCreateOpen = true;
    state.forms.workspace = options.workspaceDraftFromItem?.(selectedWorkspace, diagnostics) || state.forms.workspace;
    state.formErrors.workspace = "";
    options.render?.();
    return true;
  }

  const cancelWorkspaceEdit = target.closest("[data-action='cancel-workspace-edit']");
  if (cancelWorkspaceEdit) {
    event.preventDefault();
    state.editingWorkspaceID = "";
    state.workspaceCreateOpen = false;
    state.forms.workspace = options.defaultWorkspaceDraft?.() || state.forms.workspace;
    state.formErrors.workspace = "";
    options.render?.();
    return true;
  }

  const deleteWorkspace = target.closest("[data-action='delete-workspace']");
  if (deleteWorkspace) {
    event.preventDefault();
    const workspaceID = deleteWorkspace.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    if (!window.confirm(`Delete workspace "${selectedWorkspace.name}" and all its turns/threads/artifacts?`)) {
      return true;
    }
    options.setStatus?.(`Deleting ${selectedWorkspace.name}...`, "busy");
    await apiDelete(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}?force=1`);
    options.clearWorkspaceDiagnostics?.(workspaceID);
    if (state.editingWorkspaceID === workspaceID) {
      state.editingWorkspaceID = "";
      state.forms.workspace = options.defaultWorkspaceDraft?.() || state.forms.workspace;
      state.formErrors.workspace = "";
    }
    await options.loadBaseData?.();
    options.setStatus?.("Workspace deleted.", "ok");
    options.render?.();
    return true;
  }

  const initializeWorkspace = target.closest("[data-action='initialize-workspace']");
  if (initializeWorkspace) {
    event.preventDefault();
    const workspaceID = initializeWorkspace.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    options.setStatus?.(`Initializing ${selectedWorkspace.name}...`, "busy");
    await apiPostJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/initialize`, {});
    await options.loadBaseData?.();
    options.setStatus?.("Workspace initialized.", "ok");
    options.render?.();
    return true;
  }

  const syncWorkspace = target.closest("[data-action='sync-workspace']");
  if (syncWorkspace) {
    event.preventDefault();
    const workspaceID = syncWorkspace.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    options.setStatus?.(`Syncing ${selectedWorkspace.name}...`, "busy");
    await apiPostJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/sync`, {});
    await options.loadBaseData?.();
    options.setStatus?.("Workspace synced.", "ok");
    options.render?.();
    return true;
  }

  const recoverWorkspace = target.closest("[data-action='recover-workspace']");
  if (recoverWorkspace) {
    event.preventDefault();
    const workspaceID = recoverWorkspace.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    options.setStatus?.(`Recovering ${selectedWorkspace.name}...`, "busy");
    await apiPostJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/recover`, {});
    await options.loadBaseData?.();
    options.setStatus?.("Workspace recovered.", "ok");
    options.render?.();
    return true;
  }

  const pushWorkspace = target.closest("[data-action='push-workspace']");
  if (pushWorkspace) {
    event.preventDefault();
    const workspaceID = pushWorkspace.getAttribute("data-workspace-id") || "";
    state.pushWorkspaceID = state.pushWorkspaceID === workspaceID ? "" : workspaceID;
    options.render?.();
    return true;
  }

  const submitWorkspacePush = target.closest("[data-action='submit-workspace-push']");
  if (submitWorkspacePush) {
    event.preventDefault();
    const workspaceID = submitWorkspacePush.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    const branch = String(selectedWorkspace.default_branch || "main").trim() || "main";
    state.pushWorkspaceID = "";
    options.setStatus?.(`Pushing ${selectedWorkspace.name}...`, "busy");
    options.render?.();
    await apiPostJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/push`, { branch });
    await options.loadBaseData?.();
    options.setStatus?.(`Published ${branch} from ${selectedWorkspace.name}.`, "ok");
    options.render?.();
    return true;
  }

  const cancelWorkspacePush = target.closest("[data-action='cancel-workspace-push']");
  if (cancelWorkspacePush) {
    event.preventDefault();
    state.pushWorkspaceID = "";
    options.render?.();
    return true;
  }

  const connectWorkspaceRemote = target.closest("[data-action='connect-workspace-remote']");
  if (connectWorkspaceRemote) {
    event.preventDefault();
    const workspaceID = connectWorkspaceRemote.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    const diagnostics = await options.loadWorkspaceDiagnostics?.(workspaceID);
    const remoteURL = window.prompt("Remote URL", String(selectedWorkspace.remote_url || "").trim());
    if (remoteURL === null) {
      return true;
    }
    const fetchAuthRef = window.prompt("Fetch auth ref (optional)", String(diagnostics?.fetch_auth_ref || "").trim());
    if (fetchAuthRef === null) {
      return true;
    }
    const pushAuthRef = window.prompt("Push auth ref (optional)", String(diagnostics?.push_auth_ref || "").trim());
    if (pushAuthRef === null) {
      return true;
    }
    const remoteName = window.prompt("Remote name", String(selectedWorkspace.remote_name || "origin").trim());
    if (remoteName === null) {
      return true;
    }
    options.setStatus?.(`Connecting ${selectedWorkspace.name} to remote...`, "busy");
    await apiPostJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/connect-remote`, {
      remote_url: remoteURL,
      fetch_auth_ref: fetchAuthRef,
      push_auth_ref: pushAuthRef,
      remote_name: remoteName
    });
    options.clearWorkspaceDiagnostics?.(workspaceID);
    await options.loadBaseData?.();
    options.setStatus?.("Workspace remote connected.", "ok");
    options.render?.();
    return true;
  }

  const configureWorkspaceReview = target.closest("[data-action='configure-workspace-review']");
  if (configureWorkspaceReview) {
    event.preventDefault();
    const workspaceID = configureWorkspaceReview.getAttribute("data-workspace-id") || "";
    const opening = state.reviewConfigWorkspaceID !== workspaceID;
    if (opening) {
      await options.loadWorkspaceDiagnostics?.(workspaceID);
    }
    state.reviewConfigWorkspaceID = opening ? workspaceID : "";
    options.render?.();
    return true;
  }

  const submitWorkspaceReview = target.closest("[data-action='submit-workspace-review']");
  if (submitWorkspaceReview) {
    event.preventDefault();
    const workspaceID = submitWorkspaceReview.getAttribute("data-workspace-id") || "";
    const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
    if (!selectedWorkspace) {
      return true;
    }
    const form = submitWorkspaceReview.closest("[data-workspace-review-form]");
    if (!(form instanceof HTMLFormElement)) {
      return true;
    }
    const formData = new FormData(form);
    const reviewProvider = String(formData.get("review_provider") || "");
    const reviewAuthRef = String(formData.get("review_auth_ref") || "");
    options.setStatus?.(`Configuring review for ${selectedWorkspace.name}...`, "busy");
    await apiPostJSON(state.apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/configure-review`, {
      review_provider: reviewProvider,
      review_auth_ref: reviewAuthRef
    });
    state.reviewConfigWorkspaceID = "";
    options.clearWorkspaceDiagnostics?.(workspaceID);
    await options.loadBaseData?.();
    options.setStatus?.("Workspace review configured.", "ok");
    options.render?.();
    return true;
  }

  const cancelWorkspaceReview = target.closest("[data-action='cancel-workspace-review']");
  if (cancelWorkspaceReview) {
    event.preventDefault();
    state.reviewConfigWorkspaceID = "";
    options.render?.();
    return true;
  }

  const toggleWorkspaceContent = target.closest("[data-action='toggle-workspace-content']");
  if (toggleWorkspaceContent) {
    event.preventDefault();
    const workspaceID = toggleWorkspaceContent.getAttribute("data-workspace-id") || "";
    await toggleWorkspaceContentPanel(state, workspaceID, options);
    return true;
  }

  return false;
}

async function handleInferenceActions(state, target, event, options) {
  const toggleCredentialForm = target.closest("[data-action='toggle-inference-credential-form']");
  if (toggleCredentialForm) {
    event.preventDefault();
    if (state.inferenceCredentialFormOpen || state.editingInferenceCredentialID) {
      state.inferenceCredentialFormOpen = false;
      state.editingInferenceCredentialID = "";
      state.forms.inferenceCredential = options.defaultInferenceCredentialDraft?.() || state.forms.inferenceCredential;
      state.formErrors.inferenceCredential = "";
      state.inferenceCredentialRefManuallyEdited = false;
    } else {
      state.inferenceCredentialFormOpen = true;
    }
    options.render?.();
    return true;
  }

  const toggleRouteForm = target.closest("[data-action='toggle-inference-route-form']");
  if (toggleRouteForm) {
    event.preventDefault();
    if (!state.editingInferenceRouteID && (!Array.isArray(state.inferenceCredentials) || state.inferenceCredentials.length === 0)) {
      state.inferenceCredentialFormOpen = true;
      state.inferenceRouteFormOpen = false;
      state.forms.inferenceRoute = options.defaultInferenceRouteDraft?.() || state.forms.inferenceRoute;
      state.formErrors.inferenceRoute = "";
      options.render?.();
      return true;
    }
    if (state.inferenceRouteFormOpen || state.editingInferenceRouteID) {
      state.inferenceRouteFormOpen = false;
      state.editingInferenceRouteID = "";
      state.forms.inferenceRoute = options.defaultInferenceRouteDraft?.() || state.forms.inferenceRoute;
      state.formErrors.inferenceRoute = "";
    } else {
      state.inferenceRouteFormOpen = true;
    }
    options.render?.();
    return true;
  }

  const toggleCredentialEnabled = target.closest("[data-action='toggle-inference-credential-enabled']");
  if (toggleCredentialEnabled) {
    event.preventDefault();
    const credentialID = toggleCredentialEnabled.getAttribute("data-inference-credential-id") || "";
    const selected = options.lookupByID?.(state.inferenceCredentials, credentialID) || null;
    if (!selected) return true;
    options.setStatus?.("Updating credential...", "busy");
    await apiPatchJSON(state.apiKey, `/v1/inference/credentials/${encodeURIComponent(credentialID)}`, { enabled: !selected.enabled });
    await options.loadBaseData?.();
    options.setStatus?.(selected.enabled ? "Credential disabled." : "Credential enabled.", "ok");
    options.render?.();
    return true;
  }

  const toggleRouteEnabled = target.closest("[data-action='toggle-inference-route-enabled']");
  if (toggleRouteEnabled) {
    event.preventDefault();
    const routeID = toggleRouteEnabled.getAttribute("data-inference-route-id") || "";
    const selected = options.lookupByID?.(state.inferenceRoutes, routeID) || null;
    if (!selected) return true;
    options.setStatus?.("Updating route...", "busy");
    await apiPatchJSON(state.apiKey, `/v1/inference/routes/${encodeURIComponent(routeID)}`, { enabled: !selected.enabled });
    await options.loadBaseData?.();
    options.setStatus?.(selected.enabled ? "Route disabled." : "Route enabled.", "ok");
    options.render?.();
    return true;
  }

  const trafficFilter = target.closest("[data-action='inference-traffic-filter']");
  if (trafficFilter) {
    event.preventDefault();
    state.inferenceTrafficFilter = trafficFilter.getAttribute("data-filter") || "all";
    options.render?.();
    return true;
  }

  const trafficExpand = target.closest("[data-action='inference-traffic-expand']");
  if (trafficExpand) {
    event.preventDefault();
    state.inferenceTrafficExpanded = true;
    options.render?.();
    return true;
  }

  const playgroundToggleRaw = target.closest("[data-action='inference-playground-toggle-raw']");
  if (playgroundToggleRaw) {
    event.preventDefault();
    state.inferencePlaygroundShowRawJSON = !state.inferencePlaygroundShowRawJSON;
    options.render?.();
    return true;
  }

  const editCredential = target.closest("[data-action='edit-inference-credential']");
  if (editCredential) {
    event.preventDefault();
    const credentialID = editCredential.getAttribute("data-inference-credential-id") || "";
    const selected = options.lookupByID?.(state.inferenceCredentials, credentialID) || null;
    if (!selected) {
      return true;
    }
    state.inferenceCredentialFormOpen = true;
    state.editingInferenceCredentialID = selected.id;
    state.forms.inferenceCredential = options.inferenceCredentialDraftFromItem?.(selected) || state.forms.inferenceCredential;
    state.formErrors.inferenceCredential = "";
    options.render?.();
    return true;
  }

  const cancelCredentialEdit = target.closest("[data-action='cancel-inference-credential-edit']");
  if (cancelCredentialEdit) {
    event.preventDefault();
    state.inferenceCredentialFormOpen = false;
    state.editingInferenceCredentialID = "";
    state.forms.inferenceCredential = options.defaultInferenceCredentialDraft?.() || state.forms.inferenceCredential;
    state.formErrors.inferenceCredential = "";
    state.inferenceCredentialRefManuallyEdited = false;
    options.render?.();
    return true;
  }

  const deleteCredential = target.closest("[data-action='delete-inference-credential']");
  if (deleteCredential) {
    event.preventDefault();
    const credentialID = deleteCredential.getAttribute("data-inference-credential-id") || "";
    const selected = options.lookupByID?.(state.inferenceCredentials, credentialID) || null;
    if (!selected) {
      return true;
    }
    if (!window.confirm(`Delete credential "${selected.name}"?`)) {
      return true;
    }
    options.setStatus?.(`Deleting ${selected.name}...`, "busy");
    await apiDelete(state.apiKey, `/v1/inference/credentials/${encodeURIComponent(credentialID)}`);
    if (state.editingInferenceCredentialID === credentialID) {
      state.editingInferenceCredentialID = "";
      state.forms.inferenceCredential = options.defaultInferenceCredentialDraft?.() || state.forms.inferenceCredential;
      state.formErrors.inferenceCredential = "";
    }
    await options.loadBaseData?.();
    options.setStatus?.("Credential deleted.", "ok");
    options.render?.();
    return true;
  }

  const editRoute = target.closest("[data-action='edit-inference-route']");
  if (editRoute) {
    event.preventDefault();
    const routeID = editRoute.getAttribute("data-inference-route-id") || "";
    const selected = options.lookupByID?.(state.inferenceRoutes, routeID) || null;
    if (!selected) {
      return true;
    }
    state.inferenceRouteFormOpen = true;
    state.editingInferenceRouteID = selected.id;
    state.forms.inferenceRoute = options.inferenceRouteDraftFromItem?.(selected) || state.forms.inferenceRoute;
    state.formErrors.inferenceRoute = "";
    options.render?.();
    return true;
  }

  const cancelRouteEdit = target.closest("[data-action='cancel-inference-route-edit']");
  if (cancelRouteEdit) {
    event.preventDefault();
    state.inferenceRouteFormOpen = false;
    state.editingInferenceRouteID = "";
    state.forms.inferenceRoute = options.defaultInferenceRouteDraft?.() || state.forms.inferenceRoute;
    state.formErrors.inferenceRoute = "";
    options.render?.();
    return true;
  }

  const deleteRoute = target.closest("[data-action='delete-inference-route']");
  if (deleteRoute) {
    event.preventDefault();
    const routeID = deleteRoute.getAttribute("data-inference-route-id") || "";
    const selected = options.lookupByID?.(state.inferenceRoutes, routeID) || null;
    if (!selected) {
      return true;
    }
    if (!window.confirm(`Delete route "${selected.name}"?`)) {
      return true;
    }
    options.setStatus?.(`Deleting ${selected.name}...`, "busy");
    await apiDelete(state.apiKey, `/v1/inference/routes/${encodeURIComponent(routeID)}`);
    if (state.editingInferenceRouteID === routeID) {
      state.editingInferenceRouteID = "";
      state.forms.inferenceRoute = options.defaultInferenceRouteDraft?.() || state.forms.inferenceRoute;
      state.formErrors.inferenceRoute = "";
    }
    await options.loadBaseData?.();
    options.setStatus?.("Route deleted.", "ok");
    options.render?.();
    return true;
  }

  const clearPlayground = target.closest("[data-action='clear-inference-playground']");
  if (clearPlayground) {
    event.preventDefault();
    state.inferencePlaygroundResponse = null;
    options.render?.();
    return true;
  }

  return false;
}

function parseCSVString(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

async function submitPromotion(state, targetBranch, mode, description, options) {
  options.requireAPIKey?.();
  const currentThread = state.threadDetail.thread;
  if (!currentThread) {
    throw new Error("Select a thread first.");
  }
  if (!targetBranch) {
    throw new Error("target_branch is required");
  }
  if (!mode) {
    throw new Error("mode is required");
  }
  const busyLabel = mode === "remote_pr"
    ? "Opening merge request..."
    : mode === "remote_push"
      ? "Merging and pushing draft..."
      : "Merging draft...";
  options.setStatus?.(busyLabel, "busy");
  const created = await apiPostJSON(state.apiKey, `/v1/threads/${encodeURIComponent(currentThread.id)}/promotions`, {
    target_branch: targetBranch,
    mode,
    description: description || ""
  });
  await options.loadBaseData?.();
  await loadThreadComponent(state.threadDetail, state.apiKey, currentThread.id);
  const okMessage = mode === "remote_pr"
    ? (created.state === "running" ? "Merge request opened." : "Remote review recorded.")
    : mode === "remote_push"
      ? (created.state === "succeeded" ? "Draft merged and pushed." : "Merge and push recorded.")
      : (created.state === "succeeded" ? "Draft merged." : "Merge recorded.");
  options.setStatus?.(created.state === "conflicted" ? "Merge conflicted." : okMessage, created.state === "failed" ? "error" : "ok");
  options.render?.();
}

async function retryPromotion(state, promotionID, options) {
  options.requireAPIKey?.();
  if (!promotionID) {
    throw new Error("promotion_id is required");
  }
  options.setStatus?.("Retrying promotion...", "busy");
  const updated = await apiPostJSON(state.apiKey, `/v1/promotions/${encodeURIComponent(promotionID)}/retry`, {});
  await options.loadBaseData?.();
  if (state.threadDetail.thread) {
    await loadThreadComponent(state.threadDetail, state.apiKey, state.threadDetail.thread.id);
  }
  options.setStatus?.(
    updated.state === "succeeded" ? "Promotion retried successfully." : "Retry recorded.",
    updated.state === "failed" ? "error" : "ok"
  );
  options.render?.();
}

async function cancelPromotion(state, promotionID, options) {
  options.requireAPIKey?.();
  if (!promotionID) {
    throw new Error("promotion_id is required");
  }
  options.setStatus?.("Canceling remote review...", "busy");
  const updated = await apiPostJSON(state.apiKey, `/v1/promotions/${encodeURIComponent(promotionID)}/cancel`, {});
  await options.loadBaseData?.();
  if (state.threadDetail.thread) {
    await loadThreadComponent(state.threadDetail, state.apiKey, state.threadDetail.thread.id);
  }
  options.setStatus?.(
    updated.state === "canceled" ? "Remote review canceled locally." : "Cancel recorded.",
    updated.state === "failed" ? "error" : "ok"
  );
  options.render?.();
}

async function submitDecisionResponse(state, target, options) {
  options.requireAPIKey?.();
  const decisionID = target.getAttribute("data-decision-id") || "";
  const action = target.getAttribute("data-decision-action") || "";
  const decisionItem = options.lookupDecision?.(decisionID) || null;
  if (!decisionItem) {
    throw new Error("Decision not found.");
  }
  if (!action) {
    throw new Error("decision action is required");
  }
  const form = target.closest("[data-decision-id]");
  const values = collectDecisionResponseValues(state.decisionDrafts, form, decisionItem);
  options.setStatus?.("Submitting decision...", "busy");
  await apiPostJSON(state.apiKey, `/v1/decisions/${encodeURIComponent(decisionID)}/respond`, {
    action,
    values,
    resolved_by: "builtin-web"
  });
  clearDecisionDraft(state.decisionDrafts, decisionID);
  await refreshAfterDecisionMutation(state, decisionItem, options);
  options.setStatus?.("Decision submitted.", "ok");
  options.render?.();
}

async function submitDecisionCancel(state, target, options) {
  options.requireAPIKey?.();
  const decisionID = target.getAttribute("data-decision-id") || "";
  const decisionItem = options.lookupDecision?.(decisionID) || null;
  if (!decisionItem) {
    throw new Error("Decision not found.");
  }
  options.setStatus?.("Canceling decision...", "busy");
  await apiPostJSON(state.apiKey, `/v1/decisions/${encodeURIComponent(decisionID)}/cancel`, {
    resolved_by: "builtin-web"
  });
  clearDecisionDraft(state.decisionDrafts, decisionID);
  await refreshAfterDecisionMutation(state, decisionItem, options);
  options.setStatus?.("Decision canceled.", "ok");
  options.render?.();
}

async function refreshAfterDecisionMutation(state, decisionItem, options) {
  await options.loadBaseData?.();
  if (state.route.id === "thread-detail" && decisionItem.thread_id) {
    await refreshThreadComponent(state.threadDetail, state.apiKey, decisionItem.thread_id, {
      refreshEventTurnIDs: decisionItem.turn_id ? [decisionItem.turn_id] : []
    });
    options.stopLiveResources?.();
    options.startLiveResources?.(state.route);
    return;
  }
  if (state.route.id === "turn-detail" && decisionItem.turn_id) {
    await refreshTurnDetail(state.turnDetail, state.apiKey, decisionItem.turn_id, options.adminTurnDetailOptions?.());
  }
}

async function toggleThreadContentPanel(state, options) {
  state.threadContentPanelOpen = !state.threadContentPanelOpen;
  if (state.threadContentPanelOpen) {
    const current = state.threadDetail.thread;
    if (current && state.workspaceContent.workspaceID !== current.workspace_id) {
      const workspaceItem = options.lookupByID?.(state.workspaces, current.workspace_id) || null;
      const defaultRef = current.draft_branch || current.last_base_ref || workspaceItem?.default_branch || "";
      cancelWorkspaceContentRequests(state.workspaceContent);
      state.workspaceContent = createWorkspaceContentState();
      state.workspaceContent.workspaceID = current.workspace_id;
      state.workspaceContent.refInput = defaultRef;
      options.render?.();
      await loadWorkspaceContentTree(state.workspaceContent, state.apiKey, current.workspace_id, {
        ref: defaultRef,
        path: "",
        render: options.render,
        request: captureRequest(state.requests.route)
      });
    } else {
      options.render?.();
    }
  } else {
    cancelWorkspaceContentRequests(state.workspaceContent);
    options.render?.();
  }
}

async function toggleWorkspaceContentPanel(state, workspaceID, options) {
  if (!workspaceID) {
    return;
  }
  if (state.workspaceContent.workspaceID === workspaceID) {
    options.resetWorkspaceContentPanel?.();
    options.render?.();
    return;
  }
  const selectedWorkspace = options.lookupByID?.(state.workspaces, workspaceID) || null;
  if (!selectedWorkspace) {
    throw new Error("Workspace not found.");
  }
  cancelWorkspaceContentRequests(state.workspaceContent);
  state.workspaceContent = createWorkspaceContentState();
  state.workspaceContent.workspaceID = workspaceID;
  state.workspaceContent.refInput = String(selectedWorkspace.default_branch || "");
  options.render?.();
  await loadWorkspaceContentTree(state.workspaceContent, state.apiKey, workspaceID, {
    ref: state.workspaceContent.refInput,
    path: "",
    render: options.render,
    request: captureRequest(state.requests.route)
  });
}
